/**
 * React-query hooks for the erp-integration-api secure_proxy whitelist.
 *
 * Consumes the thin axios client in `src/api/erp-integration-client.ts`.
 * Hooks are staff-only (the endpoints are gated behind two-layer internal-auth
 * + admin-portal user pool server-side).
 *
 * Every read/write hook accepts an explicit `orgId` because the backend
 * resolves the target organization from the `x-ivy-org-id` header (or the
 * token's default org). See the client module for details on the cross-org
 * header pattern.
 *
 * `useIntegrationsByOrg` fetches the full integrations list (with embedded
 * use cases) for the target org. `useSecureProxyUseCases` derives from the
 * same query — the backend embeds all use cases on the integration payload —
 * so the two hooks share a single network round-trip.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from '@tanstack/react-query'

import {
  getErpIntegrationClient,
  HistoryEndpointNotAvailableError,
  IntegrationWithUseCasesSummary,
  SecureProxyHistoryRow,
  SecureProxyWhitelist,
  SecureProxyWhitelistUpdate
} from '@/api/erp-integration-client'

// --- query keys ------------------------------------------------------------

export const ERP_INTEGRATION_QUERY_IDS = {
  INTEGRATIONS_BY_ORG: (orgId: string) =>
    ['erp-integration', 'integrations', orgId] as const,
  SECURE_PROXY_USE_CASES: (orgId: string, integrationId: string) =>
    ['erp-integration', 'use-cases', orgId, integrationId] as const,
  SECURE_PROXY_WHITELIST: (
    orgId: string,
    integrationId: string,
    useCaseId: string
  ) =>
    ['erp-integration', 'whitelist', orgId, integrationId, useCaseId] as const,
  SECURE_PROXY_HISTORY: (
    orgId: string,
    integrationId: string,
    useCaseId: string,
    limit: number
  ) =>
    [
      'erp-integration',
      'history',
      orgId,
      integrationId,
      useCaseId,
      limit
    ] as const
} as const

// --- public summary types --------------------------------------------------
//
// Narrow projections of the backend shapes. The dialog only needs identity +
// label for the dropdown items — keep these minimal so consumers don't couple
// to fields they shouldn't care about.

export interface IntegrationSummary {
  id: string
  name: string
}

export interface SecureProxyUseCaseSummary {
  id: string
  display_name: string
  type: 'secure_proxy'
}

// --- hooks -----------------------------------------------------------------

const client = getErpIntegrationClient()

/**
 * Fetches the secure_proxy whitelist for a given (integrationId, useCaseId).
 *
 * Disabled when any of `orgId`, `integrationId`, `useCaseId` is falsy so the
 * dialog can mount before the user has selected both ids.
 */
export const useSecureProxyWhitelist = (
  orgId: string | undefined,
  integrationId: string | undefined,
  useCaseId: string | undefined,
  opts: Partial<UseQueryOptions<SecureProxyWhitelist>> = {}
): ReturnType<typeof useQuery<SecureProxyWhitelist>> => {
  return useQuery<SecureProxyWhitelist>({
    queryKey: ERP_INTEGRATION_QUERY_IDS.SECURE_PROXY_WHITELIST(
      orgId ?? '',
      integrationId ?? '',
      useCaseId ?? ''
    ),
    queryFn: () =>
      client.getSecureProxyWhitelist(orgId!, integrationId!, useCaseId!),
    enabled: !!orgId && !!integrationId && !!useCaseId,
    refetchOnWindowFocus: false,
    retry: false,
    ...opts
  })
}

/**
 * PUTs an update for the secure_proxy whitelist. On success, invalidates the
 * matching `SECURE_PROXY_WHITELIST` query so the dialog auto-refreshes its
 * server snapshot.
 */
export const useMutationUpdateSecureProxyWhitelist = ({
  orgId,
  integrationId,
  useCaseId
}: {
  orgId: string
  integrationId: string
  useCaseId: string
}): ReturnType<
  typeof useMutation<
    SecureProxyWhitelist,
    unknown,
    SecureProxyWhitelistUpdate,
    unknown
  >
> => {
  const queryClient = useQueryClient()

  return useMutation<
    SecureProxyWhitelist,
    unknown,
    SecureProxyWhitelistUpdate,
    unknown
  >({
    mutationFn: (body: SecureProxyWhitelistUpdate) =>
      client.updateSecureProxyWhitelist(orgId, integrationId, useCaseId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ERP_INTEGRATION_QUERY_IDS.SECURE_PROXY_WHITELIST(
          orgId,
          integrationId,
          useCaseId
        )
      })
      // Partial-key invalidation (omit `limit`) so every limit-variant of the
      // history query refreshes after a successful save (Plan 04 / UI-08).
      queryClient.invalidateQueries({
        queryKey: [
          'erp-integration',
          'history',
          orgId,
          integrationId,
          useCaseId
        ]
      })
    }
  })
}

// --- history ---------------------------------------------------------------

/**
 * Return shape for `useSecureProxyHistory`. Adds `isEndpointUnavailable` on top
 * of the usual react-query fields so the panel can degrade gracefully when the
 * backend history endpoint responds 404/501 (not deployed on the current
 * stage). See D-07 in `02-CONTEXT.md`: we do NOT invent a direct-data-store
 * fallback; we surface the deferral to the user.
 */
export interface UseSecureProxyHistoryResult {
  data: SecureProxyHistoryRow[] | undefined
  isLoading: boolean
  isError: boolean
  error: unknown
  isEndpointUnavailable: boolean
}

/**
 * Fetches the recent change history for a (integrationId, useCaseId) pair.
 *
 * Disabled when any id is missing or `opts.enabled === false` so the history
 * panel can stay lazy — only fetching when the Collapsible is opened.
 *
 * Does not retry on `HistoryEndpointNotAvailableError` (404/501 from the
 * backend): it's a deploy-state signal, not a transient failure.
 */
export const useSecureProxyHistory = (
  orgId: string | undefined,
  integrationId: string | undefined,
  useCaseId: string | undefined,
  opts: { limit?: number; enabled?: boolean } = {}
): UseSecureProxyHistoryResult => {
  const limit = opts.limit ?? 10
  const externalEnabled = opts.enabled !== false
  const enabled = externalEnabled && !!orgId && !!integrationId && !!useCaseId

  const query = useQuery<SecureProxyHistoryRow[]>({
    queryKey: ERP_INTEGRATION_QUERY_IDS.SECURE_PROXY_HISTORY(
      orgId ?? '',
      integrationId ?? '',
      useCaseId ?? '',
      limit
    ),
    queryFn: () =>
      client.getSecureProxyHistory(orgId!, integrationId!, useCaseId!, limit),
    enabled,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) =>
      !(error instanceof HistoryEndpointNotAvailableError) && failureCount < 2
  })

  const isEndpointUnavailable =
    query.isError && query.error instanceof HistoryEndpointNotAvailableError

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isEndpointUnavailable
  }
}

// --- integrations / use-case pickers --------------------------------------

/**
 * Return shape for the picker hooks. Matches the fields the dialog reads:
 * `data`, `isLoading`, `isError`, plus `refetch` so the error UI can offer a
 * retry button. If the backing list endpoint errors we do NOT fall back to
 * raw-id text inputs — the dialog shows an error state because without the
 * list we can't target a whitelist edit anyway.
 */
export interface PickerListResult<T> {
  data: T[] | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

/**
 * Internal hook: fetches the raw list of integrations (with embedded use
 * cases) for the target org. Shared by both `useIntegrationsByOrg` and
 * `useSecureProxyUseCases` so selecting a use-case after an integration does
 * NOT trigger a second network call — react-query de-dupes on the same key.
 */
const useIntegrationsQuery = ({
  orgId,
  enabled = true
}: {
  orgId: string | undefined
  enabled?: boolean
}): ReturnType<typeof useQuery<IntegrationWithUseCasesSummary[]>> =>
  useQuery<IntegrationWithUseCasesSummary[]>({
    queryKey: ERP_INTEGRATION_QUERY_IDS.INTEGRATIONS_BY_ORG(orgId ?? ''),
    queryFn: () => client.listIntegrationsV2(orgId!),
    enabled: enabled && !!orgId,
    refetchOnWindowFocus: false,
    retry: false
  })

/**
 * Lists the integrations on `orgId`. Returns `{ id, name }[]` — the narrow
 * projection the dialog's Integration dropdown needs.
 */
export const useIntegrationsByOrg = ({
  orgId
}: {
  orgId: string | undefined
}): PickerListResult<IntegrationSummary> => {
  const query = useIntegrationsQuery({ orgId })

  const data =
    query.data?.map((integration) => ({
      id: integration.id,
      name: integration.name
    })) ?? undefined

  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch()
    }
  }
}

/**
 * Derives the secure_proxy use cases for a given integration from the same
 * cached `/v2/integrations` payload. The backend returns `use_cases` embedded
 * on each integration so no additional network call is needed.
 *
 * The hook is "disabled" in practice until both ids are present — it returns
 * `{ data: undefined }` so the dialog shows the standard loading/empty state
 * until a selection is made.
 */
export const useSecureProxyUseCases = ({
  orgId,
  integrationId
}: {
  orgId: string | undefined
  integrationId: string | undefined
}): PickerListResult<SecureProxyUseCaseSummary> => {
  const query = useIntegrationsQuery({
    orgId,
    enabled: !!orgId && !!integrationId
  })

  const data = (() => {
    if (!integrationId || !query.data) {
      return undefined
    }

    const integration = query.data.find((i) => i.id === integrationId)

    if (!integration) {
      return []
    }

    return integration.use_cases
      .filter((uc) => uc.type === 'secure_proxy')
      .map<SecureProxyUseCaseSummary>((uc) => ({
        id: uc.id,
        display_name: uc.name,
        type: 'secure_proxy'
      }))
  })()

  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch()
    }
  }
}
